import React from 'react';
import { motion } from 'framer-motion';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface ScheduledCampaign {
  id: number;
  title: string;
  author?: string;
  rating: string;
  description?: string;
  image: string;
  progress?: number;
  postedOn?: string[];
}

const ScheduledCampaignsCarousel: React.FC = () => {
  // Mock data - same structure as weeklyCampaigns from CampaignIdeas
  const scheduledCampaigns: ScheduledCampaign[] = [
    {
      id: 1,
      title: "Your Christmas Miracle Comes Early.",
      author: 'Olivia Wilson',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770020177820-generated_flyer.jpeg',
      progress: 75,
      postedOn: ['Facebook', 'Instagram']
    },
    {
      id: 2,
      title: 'Taste history: The ironclade Cheeseburger.',
      author: 'Jonathan Patterson',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855904461-generated_flyer.jpeg',
      progress: 60,
      postedOn: ['Facebook', 'Twitter']
    },
    {
      id: 3,
      title: 'Your Deliciously Leavened Passover Escape.',
      author: 'Takehiko Kanegi',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'http://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855746918-generated_flyer.jpeg',
      progress: 90,
      postedOn: ['Instagram']
    },
    {
      id: 4,
      title: 'Your pizza is now a cocktail',
      author: 'Takehiko Kanegi',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770032306070-generated_flyer.jpeg',
      progress: 45
    }
  ];

  const scrollContainer = (direction: 'left' | 'right') => {
    const container = document.getElementById('scheduled-campaigns-scroll');
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-medium text-[#EE4423]">
          Ongoing Campaigns
        </h2>
        <button className="text-gray-400 text-sm hover:text-[#EE4423] transition-colors">
          View More
        </button>
      </div>

      {/* Scrollable Carousel */}
      <div className="relative">
        {/* Left Scroll Button */}
        <button
          onClick={() => scrollContainer('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
        >
          <MdChevronLeft size={24} className="text-white" />
        </button>

        {/* Cards Container */}
        <div
          id="scheduled-campaigns-scroll"
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {scheduledCampaigns.map((campaign) => (
            <motion.div
              key={campaign.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-shrink-0 w-[280px] rounded-2xl overflow-hidden bg-gray-800/50 border border-gray-700/50"
              style={{
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 14px 0 #0000001a'
              }}
            >
              {/* Campaign Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={campaign.image}
                  alt={campaign.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 right-3">
                  <span className="px-3 py-1 bg-gray-700/90 text-white text-xs rounded-full">
                    {campaign.rating}
                  </span>
                </div>
                
                {/* Posted On Badges */}
                {campaign.postedOn && campaign.postedOn.length > 0 && (
                  <div className="absolute bottom-3 left-3 flex gap-2">
                    {campaign.postedOn.map((platform, index) => (
                      <span key={index} className="px-2 py-1 bg-black/70 text-white text-[10px] rounded">
                        {platform}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Campaign Info */}
              <div className="p-4">
                <h3 className="text-white font-medium text-xs mb-3">{campaign.title}</h3>
                
                {/* Progress Bar */}
                {campaign.progress !== undefined && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#EE4423] font-medium">{campaign.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-1">
                      <div
                        className="bg-gradient-to-r from-[#EE4423] to-[#ff6b4a] h-1 rounded-full transition-all duration-300"
                        style={{ width: `${campaign.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Right Scroll Button */}
        <button
          onClick={() => scrollContainer('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
        >
          <MdChevronRight size={24} className="text-white" />
        </button>
      </div>
    </div>
  );
};

export default ScheduledCampaignsCarousel;
